import type {
  InventoryLocation,
  RestaurantBranch,
  InventorySupplier,
  InventoryItemDraft,
  CatalogInventoryItem
} from './Types';
import { expandCatalogToSearchOptions } from './Utils/InventorySearch.utils';

export const MOCK_SUPPLIER: InventorySupplier = {
  id: 'sup-001-fresh-foods',
  name: 'Fresh Foods Distributors'
};

export const MOCK_RESTAURANT_NAME = 'Bella Italia';

export const MOCK_BRANCH: RestaurantBranch = {
  id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  name: 'Downtown Kitchen',
  address: '142 Market St, San Francisco, CA'
};

export const MOCK_LOCATIONS: InventoryLocation[] = [
  {
    id: 'loc-001-dry-store',
    name: 'Dry Store',
    code: 'DRY',
    locationType: 'room',
    parentLocationId: null,
    active: true
  },
  {
    id: 'loc-002-walk-in',
    name: 'Walk-in Cooler',
    code: 'COOL',
    locationType: 'room',
    parentLocationId: null,
    active: true
  },
  {
    id: 'loc-003-freezer',
    name: 'Freezer',
    code: 'FRZ',
    locationType: 'room',
    parentLocationId: null,
    active: true
  },
  {
    id: 'loc-004-prep',
    name: 'Prep Station',
    code: 'PREP',
    locationType: 'section',
    parentLocationId: null,
    active: true
  },
  {
    id: 'loc-005-shelf-a',
    name: 'Shelf A — Spices',
    code: 'SH-A',
    locationType: 'shelf',
    parentLocationId: 'loc-001-dry-store',
    active: true
  }
];

export const COMMON_UNITS = [
  { label: 'Case', value: 'cs' },
  { label: 'Box', value: 'box' },
  { label: 'Pack', value: 'pk' },
  { label: 'Dozen', value: 'doz' },
  { label: 'Bottle', value: 'btl' },
  { label: 'Pieces', value: 'pcs' },
  { label: 'Bunch', value: 'bnc' },
  { label: 'Head', value: 'hds' },
  { label: 'Pound', value: 'lb' },
  { label: 'Ounce', value: 'oz' },
  { label: 'Liter', value: 'L' },
  { label: 'Milliliter', value: 'ml' },
  { label: 'Kilogram', value: 'kg' },
  { label: 'Gram', value: 'g' }
];

export const DEFAULT_LOCATION_ID = MOCK_LOCATIONS[0].id;

export const createEmptyDraft = (): InventoryItemDraft => ({
  localId: crypto.randomUUID(),
  locationId: null,
  name: '',
  unit: 'kg',
  unitMultiplier: '',
  baseUnit: '',
  quantityOnHand: '',
  minQuantity: '',
  minOrderQty: '',
  note: '',
  imagePreview: null,
  imageName: null,
  imageFile: null,
  imageUrl: null,
  supplierUserId: ''
});

/** Simulates items in the DB — items may have multiple unit variants */
export const MOCK_CATALOG: CatalogInventoryItem[] = [
  {
    id: 'cat-001',
    name: 'Roma Tomatoes',
    internalSku: 'TOM-001',
    locationId: 'loc-002-walk-in',
    locationName: 'Walk-in Cooler',
    variants: [
      {
        unit: 'kg',
        baseUnit: 'kg',
        unitMultiplier: 1,
        quantityOnHand: 12,
        minQuantity: 5,
        stockStatus: 'IN_STOCK'
      },
      {
        unit: 'cs',
        baseUnit: 'kg',
        unitMultiplier: 8,
        variantId: 'var-cs-8kg',
        quantityOnHand: 4,
        minQuantity: 2,
        stockStatus: 'IN_STOCK'
      },
      {
        unit: 'cs',
        baseUnit: 'packs',
        unitMultiplier: 24,
        variantId: 'var-cs-24pk',
        quantityOnHand: 2,
        minQuantity: 1,
        stockStatus: 'IN_STOCK'
      },
      {
        unit: 'lb',
        baseUnit: 'lb',
        unitMultiplier: 1,
        quantityOnHand: 0,
        minQuantity: 10,
        stockStatus: 'OUT_OF_STOCK'
      }
    ]
  },
  {
    id: 'cat-002',
    name: 'Olive Oil',
    internalSku: 'OIL-002',
    locationId: 'loc-001-dry-store',
    locationName: 'Dry Store',
    variants: [
      { unit: 'L', quantityOnHand: 8, minQuantity: 3, stockStatus: 'IN_STOCK' }
    ]
  },
  {
    id: 'cat-003',
    name: 'Chicken Breast',
    internalSku: 'CHK-010',
    locationId: 'loc-002-walk-in',
    locationName: 'Walk-in Cooler',
    variants: [
      {
        unit: 'kg',
        quantityOnHand: 2,
        minQuantity: 5,
        stockStatus: 'LOW_STOCK'
      },
      { unit: 'lb', quantityOnHand: 8, minQuantity: 5, stockStatus: 'IN_STOCK' }
    ]
  },
  {
    id: 'cat-004',
    name: 'All-Purpose Flour',
    internalSku: 'FLR-100',
    locationId: 'loc-001-dry-store',
    locationName: 'Dry Store',
    variants: [
      {
        unit: 'kg',
        quantityOnHand: 25,
        minQuantity: 10,
        stockStatus: 'IN_STOCK'
      },
      { unit: 'cs', quantityOnHand: 6, minQuantity: 3, stockStatus: 'IN_STOCK' }
    ]
  },
  {
    id: 'cat-005',
    name: 'Fresh Basil',
    internalSku: 'BAS-020',
    locationId: 'loc-002-walk-in',
    locationName: 'Walk-in Cooler',
    variants: [
      {
        unit: 'bnc',
        quantityOnHand: 0,
        minQuantity: 2,
        stockStatus: 'OUT_OF_STOCK'
      }
    ]
  },
  {
    id: 'cat-006',
    name: 'Mozzarella Cheese',
    internalSku: 'CHZ-030',
    locationId: 'loc-002-walk-in',
    locationName: 'Walk-in Cooler',
    variants: [
      {
        unit: 'kg',
        quantityOnHand: 6,
        minQuantity: 4,
        stockStatus: 'IN_STOCK'
      },
      {
        unit: 'cs',
        quantityOnHand: 2,
        minQuantity: 1,
        stockStatus: 'IN_STOCK'
      },
      {
        unit: 'lb',
        quantityOnHand: 10,
        minQuantity: 4,
        stockStatus: 'IN_STOCK'
      }
    ]
  },
  {
    id: 'cat-007',
    name: 'San Marzano Tomatoes',
    internalSku: 'TOM-002',
    locationId: 'loc-001-dry-store',
    locationName: 'Dry Store',
    variants: [
      { unit: 'cs', quantityOnHand: 4, minQuantity: 2, stockStatus: 'IN_STOCK' }
    ]
  },
  {
    id: 'cat-008',
    name: 'Heavy Cream',
    internalSku: 'CRM-040',
    locationId: 'loc-002-walk-in',
    locationName: 'Walk-in Cooler',
    variants: [
      { unit: 'L', quantityOnHand: 3, minQuantity: 2, stockStatus: 'IN_STOCK' },
      { unit: 'qt', quantityOnHand: 5, minQuantity: 2, stockStatus: 'IN_STOCK' }
    ]
  }
];

/** Flattened search options (name - unit per variant) */
export const MOCK_CATALOG_ITEMS = expandCatalogToSearchOptions(MOCK_CATALOG);
